import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CTA,
  CTAType,
  CTATypeItems,
  Form,
  FormServices,
  PlayersServices,
  SmartLinkServices,
  stripCTAPrefix,
  withCTAPrefix,
} from "@/services/bedrock";
import { SmartLink } from "@/services/bedrock/dtos";
import { PaginatedCombobox, useCallToActions, usePaginatedCombobox } from "@/utils";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import {
  CtaTypeDefinition,
  CtaTypeDefinitions,
  CtaTypeGroup,
  CtaTypeGroupItems,
  formatCTAPhone,
  useCtaTypeGating,
} from "./ctaTypes";

export type FormComboboxItem = { id: number; label: string; original: Form };
export type SmartLinkComboboxItem = { id: number; label: string; original: SmartLink };

export interface CTAWizardContextValue {
  step: 1 | 2 | 3;
  selectedGroup: CtaTypeGroup | null;
  ctaType: CTAType | null;
  definition: CtaTypeDefinition | null;
  label: string;
  setLabel: (value: string) => void;
  linkValue: string;
  setLinkValue: React.Dispatch<React.SetStateAction<string>>;
  labelError: string | null;
  setLabelError: (value: string | null) => void;
  linkError: string | null;
  setLinkError: (value: string | null) => void;
  typeError: string | null;
  isPlayer: boolean;
  availableTypes: CTAType[];
  availableGroups: CtaTypeGroup[];
  groupTypes: CTAType[];
  recentQuickPicks: CTA[];
  forms: PaginatedCombobox<FormComboboxItem>;
  smartLinks: PaginatedCombobox<SmartLinkComboboxItem>;
  formResolvedUrl: string | null;
  smartLinkResolvedUrl: string | null;
  resolvedPreview: string | null;
  destinationSummary: string | null;
  missingValueWarning: string | false | undefined;
  applyTypeSelection: (type: CTAType) => void;
  handleSelectGroup: (group: CtaTypeGroup) => void;
  handleSelectRecent: (recent: CTA) => void;
  handleBackFromConfigure: () => void;
  handleNextFromConfigure: () => void;
  handleConfirm: () => void;
  setStep: (step: 1 | 2 | 3) => void;
}

const CTAWizardContext = React.createContext<CTAWizardContextValue | null>(null);

export const useCTAWizard = (): CTAWizardContextValue => {
  const value = React.useContext(CTAWizardContext);
  if (!value) throw new Error("useCTAWizard must be used within CTAWizardProvider");
  return value;
};

interface CTAWizardProviderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialCta: CTA | null;
  playerId?: number;
  onConfirm: (cta: CTA) => void;
  children: React.ReactNode;
}

export const CTAWizardProvider: React.FC<CTAWizardProviderProps> = ({
  open,
  onOpenChange,
  initialCta,
  playerId,
  onConfirm,
  children,
}) => {
  const { isPlayer } = useSocialCoachSession();
  const { isTypeAvailable } = useCtaTypeGating();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedGroup, setSelectedGroup] = useState<CtaTypeGroup | null>(null);
  const [ctaType, setCtaType] = useState<CTAType | null>(null);
  const [label, setLabel] = useState("");
  const [linkValue, setLinkValue] = useState("");
  const [labelError, setLabelError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);

  const definition = ctaType ? CtaTypeDefinitions[ctaType] : null;

  const availableTypes = useMemo(() => CTATypeItems.filter(isTypeAvailable), [isTypeAvailable]);

  const availableGroups = useMemo(
    () => CtaTypeGroupItems.filter(group => availableTypes.some(type => CtaTypeDefinitions[type].group === group)),
    [availableTypes]
  );

  const groupTypes = useMemo(
    () => (selectedGroup ? availableTypes.filter(type => CtaTypeDefinitions[type].group === selectedGroup) : []),
    [availableTypes, selectedGroup]
  );

  const { data: initialForm } = useQuery({
    queryKey: ["ctaWizardForm", initialCta?.formId],
    queryFn: () => FormServices.getForm(initialCta!.formId!),
    enabled: open && !!initialCta?.formId,
  });
  const { data: initialSmartLink } = useQuery({
    queryKey: ["ctaWizardSmartLink", initialCta?.smartLinkId],
    queryFn: () => SmartLinkServices.getSmartLink(initialCta!.smartLinkId!),
    enabled: open && !!initialCta?.smartLinkId,
  });

  const initialFormItem = useMemo<FormComboboxItem | null>(
    () =>
      initialForm
        ? { id: initialForm.id, label: initialForm.displayName || "Untitled Form", original: initialForm }
        : null,
    [initialForm]
  );
  const initialSmartLinkItem = useMemo<SmartLinkComboboxItem | null>(
    () =>
      initialSmartLink ? { id: initialSmartLink.id, label: initialSmartLink.name, original: initialSmartLink } : null,
    [initialSmartLink]
  );

  const fetchForms = useCallback(async (search: string | undefined, page: number) => {
    const result = await FormServices.getForms({ search, active: true, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(form => ({
        id: form.id,
        label: form.displayName || "Untitled Form",
        original: form,
      })),
    };
  }, []);
  const forms = usePaginatedCombobox<FormComboboxItem>(fetchForms, false, initialFormItem);

  const fetchSmartLinks = useCallback(async (search: string | undefined, page: number) => {
    const result = await SmartLinkServices.getSmartLinks({ search, isActive: true, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(smartLink => ({
        id: smartLink.id,
        label: smartLink.name,
        original: smartLink,
      })),
    };
  }, []);
  const smartLinks = usePaginatedCombobox<SmartLinkComboboxItem>(fetchSmartLinks, false, initialSmartLinkItem);

  const { data: player } = useQuery({
    queryKey: ["ctaWizardPlayer", playerId],
    queryFn: () => PlayersServices.getPlayer(playerId!, ["webAddress", "shortLink", "hasPlayerCard", "playerCardUrl"]),
    enabled: open && !!playerId,
  });

  const { callToActions: recentCtas } = useCallToActions({ enabled: open, distinct: true });
  const recentQuickPicks = useMemo(
    () => recentCtas.filter(recent => isTypeAvailable(recent.type)),
    [recentCtas, isTypeAvailable]
  );

  const initialCtaRef = React.useRef(initialCta);
  initialCtaRef.current = initialCta;

  // Reset only when the dialog opens. Form/smart-link comboboxes sync via initialValue.
  useEffect(() => {
    if (!open) return;
    const cta = initialCtaRef.current;
    const initialType = cta?.type ?? null;
    const initialGroup = initialType ? CtaTypeDefinitions[initialType]?.group ?? null : null;
    setStep(cta ? 2 : 1);
    setSelectedGroup(initialGroup);
    setCtaType(initialType);
    setLabel(cta?.label ?? "");
    setLinkValue(stripCTAPrefix(cta?.customLink, initialType ? CtaTypeDefinitions[initialType]?.prefix : undefined));
    setLabelError(null);
    setLinkError(null);
    setTypeError(null);
  }, [open]);

  const clearLinkInputs = () => {
    setLinkValue("");
    setLinkError(null);
    forms.onValueChange(undefined, undefined);
    smartLinks.onValueChange(undefined, undefined);
  };

  const labelMatchesTypeDefault = (type: CTAType | null) =>
    type != null && label === CtaTypeDefinitions[type].defaultLabel;

  const applyTypeSelection = (type: CTAType) => {
    if (type !== ctaType) {
      clearLinkInputs();
      if (!initialCta || initialCta.type !== type) {
        if (!label.trim() || labelMatchesTypeDefault(ctaType)) {
          setLabel(CtaTypeDefinitions[type].defaultLabel);
        }
      } else {
        setLabel(initialCta.label);
        setLinkValue(stripCTAPrefix(initialCta.customLink, CtaTypeDefinitions[type].prefix));
        if (initialCta.formId && initialFormItem) forms.onValueChange(initialFormItem, undefined);
        if (initialCta.smartLinkId && initialSmartLinkItem) smartLinks.onValueChange(initialSmartLinkItem, undefined);
      }
    }
    setCtaType(type);
    setTypeError(null);
  };

  const handleSelectGroup = (group: CtaTypeGroup) => {
    setSelectedGroup(group);
    if (!(ctaType && CtaTypeDefinitions[ctaType]?.group === group)) {
      if (initialCta && CtaTypeDefinitions[initialCta.type]?.group === group) {
        applyTypeSelection(initialCta.type);
      } else {
        setCtaType(null);
        clearLinkInputs();
      }
    }
    setTypeError(null);
    setStep(2);
  };

  const handleSelectRecent = (recent: CTA) => {
    onConfirm({
      label: recent.label,
      type: recent.type,
      customLink: recent.customLink,
      formId: recent.formId ?? null,
      smartLinkId: recent.smartLinkId ?? null,
      destinationLabel: recent.destinationLabel ?? null,
    });
    onOpenChange(false);
  };

  const handleBackFromConfigure = () => {
    if (!label.trim() || labelMatchesTypeDefault(ctaType)) setLabel("");
    setStep(1);
  };

  const validateConfiguration = (): boolean => {
    if (!ctaType || !definition) {
      setTypeError("Please choose an option");
      return false;
    }
    let valid = true;

    if (!label.trim()) {
      setLabelError("Label is required");
      valid = false;
    } else {
      setLabelError(null);
    }

    if (definition.validate) {
      const valueToValidate = definition.input === "phone" ? formatCTAPhone(linkValue) : linkValue;
      if (definition.input === "phone" && valueToValidate !== linkValue) {
        setLinkValue(valueToValidate);
      }
      const result = definition.validate(valueToValidate);
      if (result !== true) {
        setLinkError(result);
        valid = false;
      } else {
        setLinkError(null);
      }
    } else if (definition.input === "form" && !forms.value) {
      setLinkError("Please choose a form");
      valid = false;
    } else if (definition.input === "smartLink" && !smartLinks.value) {
      setLinkError("Please choose a Smart Link");
      valid = false;
    } else {
      setLinkError(null);
    }

    return valid;
  };

  const handleNextFromConfigure = () => {
    if (validateConfiguration()) setStep(3);
  };

  const handleConfirm = () => {
    if (!ctaType || !definition) return;
    const normalizedLink = definition.input === "phone" ? formatCTAPhone(linkValue) : linkValue;
    const destinationLabel =
      ctaType === "form_link"
        ? forms.value?.label ?? null
        : ctaType === "smart_link"
        ? smartLinks.value?.label ?? null
        : definition.validate
        ? normalizedLink || null
        : null;
    onConfirm({
      label: label.trim(),
      type: ctaType,
      customLink: definition.validate ? withCTAPrefix(normalizedLink, definition.prefix) : null,
      formId: ctaType === "form_link" ? Number(forms.value?.id) : null,
      smartLinkId: ctaType === "smart_link" ? Number(smartLinks.value?.id) : null,
      destinationLabel,
    });
    onOpenChange(false);
  };

  const resolvedPreview = useMemo(() => {
    if (!definition || definition.input !== "resolved" || !player || !definition.resolvePreview) return null;
    return definition.resolvePreview(player) || null;
  }, [definition, player]);

  const showMissingValueWarning =
    !!definition &&
    definition.input === "resolved" &&
    !!playerId &&
    !!player &&
    !!definition.resolvePreview &&
    !resolvedPreview;

  const { data: selectedFormDetails } = useQuery({
    queryKey: ["ctaWizardSelectedForm", forms.value?.id],
    queryFn: () => FormServices.getForm(forms.value!.id),
    enabled: open && definition?.input === "form" && !!forms.value?.id && !forms.value.original?.testRequest?.url,
  });

  const formResolvedUrl = forms.value?.original?.testRequest?.url ?? selectedFormDetails?.testRequest?.url ?? null;
  const smartLinkResolvedUrl = smartLinks.value?.original?.url ?? smartLinks.value?.original?.destinationUrl ?? null;

  const destinationSummary = useMemo(() => {
    if (!definition) return null;
    switch (definition.input) {
      case "link":
      case "phone":
      case "email":
        return linkValue;
      case "form":
        return formResolvedUrl ?? forms.value?.label ?? null;
      case "smartLink":
        return smartLinkResolvedUrl ?? smartLinks.value?.label ?? null;
      case "resolved":
        return resolvedPreview ?? "Resolved automatically for each Player";
    }
  }, [definition, linkValue, forms.value, smartLinks.value, resolvedPreview, formResolvedUrl, smartLinkResolvedUrl]);

  const missingValueWarning = showMissingValueWarning && definition?.missingValueWarning?.(isPlayer);

  const value: CTAWizardContextValue = {
    step,
    selectedGroup,
    ctaType,
    definition,
    label,
    setLabel,
    linkValue,
    setLinkValue,
    labelError,
    setLabelError,
    linkError,
    setLinkError,
    typeError,
    isPlayer,
    availableTypes,
    availableGroups,
    groupTypes,
    recentQuickPicks,
    forms,
    smartLinks,
    formResolvedUrl,
    smartLinkResolvedUrl,
    resolvedPreview,
    destinationSummary,
    missingValueWarning,
    applyTypeSelection,
    handleSelectGroup,
    handleSelectRecent,
    handleBackFromConfigure,
    handleNextFromConfigure,
    handleConfirm,
    setStep,
  };

  return <CTAWizardContext.Provider value={value}>{children}</CTAWizardContext.Provider>;
};
